import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  createSuperAdminOht,
  SuperAdminOhtApiError,
  validateOhtCode,
  validateOhtName,
} from "../../../../api/superAdminOhts";
import "./SuperAdminOhtCreatePage.css";

/**
 * Super Admin — Create OHT
 *
 * Creates identity/status only. Navigator intake is never set from this form
 * and always begins disabled on the server.
 */

export default function SuperAdminOhtCreatePage() {
  const nav = useNavigate();

  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [nameError, setNameError] = useState<string | null>(null);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting) return;

    const nextNameError = validateOhtName(name);
    const nextCodeError = validateOhtCode(code);
    setNameError(nextNameError);
    setCodeError(nextCodeError);
    setFormError(null);

    if (nextNameError || nextCodeError) return;

    setSubmitting(true);
    try {
      const oht = await createSuperAdminOht({
        name: name.trim(),
        code: code.trim(),
        isActive,
      });

      nav(`/portal/super-admin/ohts/${encodeURIComponent(String(oht.ohtId))}`, {
        replace: true,
        state: { ohtCreated: true },
      });
    } catch (err: any) {
      if (err instanceof SuperAdminOhtApiError && err.status === 409) {
        setCodeError(
          err.message ||
            "An Ontario Health Team with this code already exists."
        );
        setFormError(null);
      } else {
        setFormError(err?.message || "Failed to create OHT.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="sa-oht-create">
      <div className="sa-oht-create__header">
        <div className="sa-oht-create__titleWrap">
          <div className="sa-oht-create__eyebrow">OHT governance</div>
          <h1 className="sa-oht-create__title">Create Ontario Health Team</h1>
          <p className="sa-oht-create__subtitle">
            Create OHT identity and organization status. Coverage, members, and
            Navigator intake are configured later.
          </p>
        </div>

        <Link
          to="/portal/super-admin/ohts"
          className="sa-oht-create__secondaryBtn"
        >
          ← Back to OHTs
        </Link>
      </div>

      <form className="sa-oht-create__card" onSubmit={onSubmit} noValidate>
        <div className="sa-oht-create__cardTitle">OHT identity</div>

        {formError ? (
          <div className="sa-oht-create__error" role="alert">
            {formError}
          </div>
        ) : null}

        <div className="sa-oht-create__fields">
          <div className="sa-oht-create__field">
            <label className="sa-oht-create__label" htmlFor="oht-create-name">
              OHT name
            </label>
            <input
              id="oht-create-name"
              type="text"
              className="sa-oht-create__input"
              value={name}
              maxLength={255}
              disabled={submitting}
              autoComplete="off"
              onChange={(e) => {
                setName(e.target.value);
                if (nameError) setNameError(null);
              }}
            />
            {nameError ? (
              <div className="sa-oht-create__fieldError" role="alert">
                {nameError}
              </div>
            ) : null}
          </div>

          <div className="sa-oht-create__field">
            <label className="sa-oht-create__label" htmlFor="oht-create-code">
              OHT code
            </label>
            <input
              id="oht-create-code"
              type="text"
              className="sa-oht-create__input"
              value={code}
              maxLength={50}
              disabled={submitting}
              autoComplete="off"
              onChange={(e) => {
                setCode(e.target.value);
                if (codeError) setCodeError(null);
              }}
            />
            {codeError ? (
              <div className="sa-oht-create__fieldError" role="alert">
                {codeError}
              </div>
            ) : (
              <div className="sa-oht-create__hint">
                Codes must be unique. Case is preserved as entered.
              </div>
            )}
          </div>

          <fieldset className="sa-oht-create__fieldset">
            <legend className="sa-oht-create__label">Organization status</legend>
            <div className="sa-oht-create__radioRow">
              <label className="sa-oht-create__radio">
                <input
                  type="radio"
                  name="oht-create-status"
                  checked={isActive === true}
                  disabled={submitting}
                  onChange={() => setIsActive(true)}
                />
                Active
              </label>
              <label className="sa-oht-create__radio">
                <input
                  type="radio"
                  name="oht-create-status"
                  checked={isActive === false}
                  disabled={submitting}
                  onChange={() => setIsActive(false)}
                />
                Inactive
              </label>
            </div>
            <div className="sa-oht-create__hint">
              Navigator intake always starts Disabled and is managed separately.
            </div>
          </fieldset>
        </div>

        <div className="sa-oht-create__actions">
          <button
            type="button"
            className="sa-oht-create__secondaryBtn"
            disabled={submitting}
            onClick={() => nav("/portal/super-admin/ohts")}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="sa-oht-create__primaryBtn"
            disabled={submitting}
          >
            {submitting ? "Creating…" : "Create OHT"}
          </button>
        </div>
      </form>
    </div>
  );
}
